import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Button, CellText, DotsVertical, EmptyState, Menu, Table,
} from '@opendate-app/setlist';
import { DebounceInput } from 'react-debounce-input';
import useLocalSearchShortcut from '../../shared/hooks/useLocalSearchShortcut';
import ContactsSidebar from '../../Contacts/components/ContactsSidebar';
import OrganizationFormModal from './OrganizationFormModal';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import CrmAvatar from '../../shared/components/CrmAvatar';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import FilterSidebar from '../../shared/components/FilterSidebar';
import ActiveFilterBadges from '../../shared/components/ActiveFilterBadges';
import TableSettings from '../../shared/components/TableSettings';
import SaveViewButton from '../../shared/components/SaveViewButton';
import { ORGANIZATION_FILTER_GROUPS, getActiveFilterCount } from '../constants/organizationFiltersConfig';
import { ORGANIZATION_COLUMNS, SORT_OPTIONS, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/organizationColumnsConfig';
import { resolveToken } from '../../../shared/marketing/themeTokens';
import crmLabels from '../../shared/utils/crmLabels';

const TABLE_SETTINGS_ICON_STROKE = resolveToken('--sl-text-secondary', '#414651');

const axios = require('axios').default;
const qs = require('qs');

function OrganizationsPage({ csrfToken, team, customFieldDefinitions = [] }) {
  const labels = crmLabels(team);
  const orgSingularLower = labels.organization.singular.toLowerCase();
  const orgPluralLower = labels.organization.plural.toLowerCase();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filterValues, setFilterValues] = useState({});

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formModalOrganization, setFormModalOrganization] = useState(null);

  const [organizationToRemove, setOrganizationToRemove] = useState(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(getDefaultHiddenColumns());
  const [sortBy, setSortBy] = useState('name asc');

  const [savedViews, setSavedViews] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [hasViewChanges, setHasViewChanges] = useState(false);
  const baselineViewRef = useRef(null);

  const searchInputRef = useRef(null);

  useLocalSearchShortcut(searchInputRef);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters, sortBy]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchFilterValues = async () => {
      try {
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }
        const response = await axios.get(`/teams/${team.id}/crm/organizations/filter_values.json`);
        setFilterValues(response.data || {});
      } catch (e) {
        toast.error('Failed to load filter options.');
      }
    };

    fetchFilterValues();
  }, [team && team.id, csrfToken]);

  const enrichedFilterGroups = useMemo(() => {
    return ORGANIZATION_FILTER_GROUPS.map(group => ({
      ...group,
      filters: group.filters.map(filter => {
        if (filter.id === 'city' && filterValues.cities) {
          return { ...filter, options: filterValues.cities };
        }
        if (filter.id === 'state' && filterValues.states) {
          return { ...filter, options: filterValues.states };
        }
        if (filter.id === 'country' && filterValues.countries) {
          return { ...filter, options: filterValues.countries };
        }
        return filter;
      })
    }));
  }, [filterValues]);

  const buildRansackParams = () => {
    const q = {};

    if (searchTerm) q.name_or_email_cont = searchTerm;

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (key === 'city' && Array.isArray(value) && value.length > 0) {
        q.city_in = value;
      } else if (key === 'state' && Array.isArray(value) && value.length > 0) {
        q.state_in = value;
      } else if (key === 'country' && Array.isArray(value) && value.length > 0) {
        q.country_in = value;
      } else if (key === 'has_email' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.email_present = 1;
        } else if (value.includes('false') && !value.includes('true')) {
          q.email_blank = 1;
        }
      } else if (key === 'has_phone' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.phone_present = 1;
        } else if (value.includes('false') && !value.includes('true')) {
          q.phone_blank = 1;
        }
      } else if (key === 'has_website' && Array.isArray(value) && value.length > 0) {
        if (value.includes('true') && !value.includes('false')) {
          q.website_url_present = 1;
        } else if (value.includes('false') && !value.includes('true')) {
          q.website_url_blank = 1;
        }
      } else if (key === 'created_at' && typeof value === 'object' && value !== null) {
        if (value.start) q.created_at_gteq = value.start;
        if (value.end) q.created_at_lteq = `${value.end}T23:59:59`;
      }
    });

    return q;
  };

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const q = buildRansackParams();
        if (sortBy) q.s = sortBy;
        const response = await axios.get(`/teams/${team.id}/crm/organizations.json`, {
          params: {
            page: currentPage,
            per_page: perPage,
            q,
          },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
        });

        const data = response.data || {};
        const organizationsFromResponse = data.organizations || [];
        const pagination = data.pagination || {};

        setOrganizations(organizationsFromResponse);
        setTotalEntries(pagination.total_entries || organizationsFromResponse.length || 0);
        setTotalPages(pagination.total_pages || 1);
      } catch (e) {
        const fallbackMessage =
          (e.response && typeof e.response.data === 'string' && e.response.data) ||
          `Failed to load ${orgPluralLower}`;
        setError(fallbackMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [team && team.id, csrfToken, currentPage, perPage, searchTerm, activeFilters, sortBy]);

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const handleApplyFilters = () => {
  };

  const handleRemoveFilter = (filterId) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterId];
    setActiveFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setActiveFilters({});
  };

  const openNewOrganizationModal = () => {
    setFormModalOrganization(null);
    setFormModalOpen(true);
  };

  const openEditModalForOrganization = (organization) => {
    setFormModalOrganization(organization);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setFormModalOrganization(null);
  };

  const handleOrganizationSaved = (saved, isNew) => {
    if (isNew) {
      setOrganizations((current) => [saved, ...current]);
      setTotalEntries((prev) => (prev || 0) + 1);
    } else {
      setOrganizations((current) => current.map((org) => (org.id === saved.id ? saved : org)));
    }
  };

  const closeRemoveModal = () => {
    setOrganizationToRemove(null);
    setRemoveSubmitting(false);
    setRemoveError(null);
  };

  const handleConfirmRemove = async () => {
    if (!organizationToRemove || !team || !team.id) return;

    try {
      setRemoveSubmitting(true);
      setRemoveError(null);
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      await axios.delete(`/teams/${team.id}/crm/organizations/${organizationToRemove.id}.json`);

      setOrganizations((prev) => prev.filter((o) => o.id !== organizationToRemove.id));
      setTotalEntries((prev) => Math.max((prev || 1) - 1, 0));
      closeRemoveModal();
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setRemoveError(resp.data.join(', '));
      } else {
        setRemoveError(`Failed to delete ${orgSingularLower}`);
      }
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((open) => !open);
  };

  const totalLabel = totalEntries === 1 ? orgSingularLower : orgPluralLower;

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    localStorage.setItem(`organizationColumns_${team.id}`, JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  useEffect(() => {
    if (!team || !team.id) return;
    const saved = localStorage.getItem(`organizationColumns_${team.id}`);
    if (saved) {
      try {
        const { visible, hidden } = JSON.parse(saved);
        const savedIds = new Set([...visible.map(c => c.id), ...hidden.map(c => c.id)]);
        const newColumns = ORGANIZATION_COLUMNS.filter(c => !savedIds.has(c.id) && !c.alwaysVisible);
        setVisibleColumns(visible);
        setHiddenColumns([...hidden, ...newColumns]);
      } catch (e) {
      }
    }
  }, [team && team.id]);

  useEffect(() => {
    if (!team || !team.id) return;
    const stored = localStorage.getItem(`organizationViews_${team.id}`);
    if (stored) {
      try {
        setSavedViews(JSON.parse(stored));
      } catch (e) {
      }
    }
  }, [team && team.id]);

  useEffect(() => {
    if (!baselineViewRef.current) {
      setHasViewChanges(false);
      return;
    }
    const baseline = baselineViewRef.current;
    const currentVisibleIds = visibleColumns.map(c => c.id).sort().join(',');
    const baselineVisibleIds = baseline.visibleColumns.sort().join(',');
    const columnsChanged = currentVisibleIds !== baselineVisibleIds;
    const filtersChanged = JSON.stringify(activeFilters) !== JSON.stringify(baseline.activeFilters);
    setHasViewChanges(columnsChanged || filtersChanged);
  }, [visibleColumns, activeFilters]);

  const handleSaveView = (viewName) => {
    const newView = {
      name: viewName,
      visibleFields: visibleColumns.map(c => c.id),
      hiddenFields: hiddenColumns.map(c => c.id),
      activeFilters: { ...activeFilters }
    };
    const updatedViews = [...savedViews, newView];
    setSavedViews(updatedViews);
    localStorage.setItem(`organizationViews_${team.id}`, JSON.stringify(updatedViews));
    setCurrentView(viewName);
    baselineViewRef.current = {
      visibleColumns: visibleColumns.map(c => c.id),
      activeFilters: { ...activeFilters }
    };
    setHasViewChanges(false);
  };

  const handleLoadView = (view) => {
    const visibleFromView = ORGANIZATION_COLUMNS.filter(c => view.visibleFields.includes(c.id));
    const hiddenFromView = ORGANIZATION_COLUMNS.filter(c => view.hiddenFields.includes(c.id));
    setVisibleColumns(visibleFromView);
    setHiddenColumns(hiddenFromView);
    setActiveFilters(view.activeFilters || {});
    setCurrentView(view.name);
    baselineViewRef.current = {
      visibleColumns: view.visibleFields,
      activeFilters: view.activeFilters || {}
    };
    setHasViewChanges(false);
    localStorage.setItem(`organizationColumns_${team.id}`, JSON.stringify({
      visible: visibleFromView,
      hidden: hiddenFromView,
    }));
  };

  const handleDeleteView = (view) => {
    const updatedViews = savedViews.filter(v => v.name !== view.name);
    setSavedViews(updatedViews);
    localStorage.setItem(`organizationViews_${team.id}`, JSON.stringify(updatedViews));
    if (currentView === view.name) {
      setCurrentView(null);
      baselineViewRef.current = null;
      setHasViewChanges(false);
    }
  };

  const formatCreatedAt = (createdAt) => (createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(team.time_zone ? { timeZone: team.time_zone } : {}),
    })
    : '—');

  const renderOrganizationCell = (organization) => {
    const name = organization.name || '(No name)';
    return (
      <div className="team-member-info">
        <CrmAvatar name={organization.name} title={name} avatarUrl={organization.avatar_thumb_url} />
        <CellText
          primary={(
            <Button as="a" href={`/teams/${team.id}/crm/organizations/${organization.id}`} variant="link-color">
              {name}
            </Button>
          )}
          supporting={organization.website_url || organization.email || undefined}
        />
      </div>
    );
  };

  const COLUMN_RENDERERS = {
    organization: renderOrganizationCell,
    contacts_count: (o) => (o.contacts_count != null ? o.contacts_count.toLocaleString() : '—'),
    location: (o) => [o.city, o.state, o.country].filter(Boolean).join(', ') || '—',
    city: (o) => o.city || '—',
    state: (o) => o.state || '—',
    created_at: (o) => formatCreatedAt(o.created_at),
    country: (o) => o.country || '—',
    email: (o) => o.email || '—',
    phone: (o) => o.phone || '—',
    website: (o) => o.website_url || '—',
    contact: (o) => o.email || o.phone || '--',
  };

  const tableColumns = [
    ...visibleColumns
      .filter((col) => COLUMN_RENDERERS[col.id])
      .map((col) => ({
        key: col.id,
        header: col.id === 'organization' ? labels.organization.singular : col.label,
        primary: col.id === 'organization',
        render: COLUMN_RENDERERS[col.id],
      })),
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '48px',
      render: (organization) => (
        <Menu
          placement="bottom-end"
          trigger={(
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`More actions for ${organization.name || '(No name)'}`}
            />
          )}
          items={[
            { label: `Edit ${orgSingularLower}`, onClick: () => openEditModalForOrganization(organization) },
            { divider: true },
            { label: `Delete ${orgSingularLower}`, destructive: true, onClick: () => setOrganizationToRemove(organization) },
          ]}
        />
      ),
    },
  ];

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="organizations"
      mainTopMargin={32}
      title={labels.organization.plural}
      subtitle={orgPluralLower === 'organizations' ? 'Manage agencies, promoters, venues, and other organizations you work with.' : `Manage the ${orgPluralLower} you work with.`}
      primaryAction={{
        label: `Add ${orgSingularLower}`,
        onClick: openNewOrganizationModal,
      }}
    >
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="border-bottom px-4 py-3">
                <div className="d-flex justify-content-between align-items-end crm-toolbar">
                  <div className="flex-grow-1 me-3 crm-toolbar-search">
                    <CrmSearchInput
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={`Search ${labels.organization.plural}`}
                      ariaLabel={`Search ${labels.organization.plural}`}
                      inputRef={searchInputRef}
                      useDebounce
                    />
                  </div>

                  <div className="team-search-filters-right d-flex align-items-center gap-2 crm-toolbar-actions">
                    <SaveViewButton
                      hasChanges={hasViewChanges}
                      savedViews={savedViews}
                      currentView={currentView}
                      onSaveView={handleSaveView}
                      onLoadView={handleLoadView}
                      onDeleteView={handleDeleteView}
                    />
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn btn-setlist-secondary"
                        onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M5 7.5V2.5M5 17.5V12.5M5 12.5C5.46499 12.5 5.69748 12.5 5.88823 12.4239C6.1305 12.3224 6.32239 12.1305 6.42388 11.8882C6.5 11.6975 6.5 11.465 6.5 11C6.5 10.535 6.5 10.3025 6.42388 10.1118C6.32239 9.8695 6.1305 9.67761 5.88823 9.57612C5.69748 9.5 5.46499 9.5 5 9.5C4.53501 9.5 4.30252 9.5 4.11177 9.57612C3.8695 9.67761 3.67761 9.8695 3.57612 10.1118C3.5 10.3025 3.5 10.535 3.5 11C3.5 11.465 3.5 11.6975 3.57612 11.8882C3.67761 12.1305 3.8695 12.3224 4.11177 12.4239C4.30252 12.5 4.53501 12.5 5 12.5ZM10 17.5V10M10 10C10.465 10 10.6975 10 10.8882 9.92388C11.1305 9.82239 11.3224 9.6305 11.4239 9.38823C11.5 9.19748 11.5 8.96499 11.5 8.5C11.5 8.03501 11.5 7.80252 11.4239 7.61177C11.3224 7.3695 11.1305 7.17761 10.8882 7.07612C10.6975 7 10.465 7 10 7C9.53501 7 9.30252 7 9.11177 7.07612C8.8695 7.17761 8.67761 7.3695 8.57612 7.61177C8.5 7.80252 8.5 8.03501 8.5 8.5C8.5 8.96499 8.5 9.19748 8.57612 9.38823C8.67761 9.6305 8.8695 9.82239 9.11177 9.92388C9.30252 10 9.53501 10 10 10ZM10 7V2.5M15 17.5V15M15 15C15.465 15 15.6975 15 15.8882 14.9239C16.1305 14.8224 16.3224 14.6305 16.4239 14.3882C16.5 14.1975 16.5 13.965 16.5 13.5C16.5 13.035 16.5 12.8025 16.4239 12.6118C16.3224 12.3695 16.1305 12.1776 15.8882 12.0761C15.6975 12 15.465 12 15 12C14.535 12 14.3025 12 14.1118 12.0761C13.8695 12.1776 13.6776 12.3695 13.5761 12.6118C13.5 12.8025 13.5 13.035 13.5 13.5C13.5 13.965 13.5 14.1975 13.5761 14.3882C13.6776 14.6305 13.8695 14.8224 14.1118 14.9239C14.3025 15 14.535 15 15 15ZM15 12V2.5"
                            stroke={TABLE_SETTINGS_ICON_STROKE}
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Table Settings</span>
                      </button>
                      <TableSettings
                        isOpen={tableSettingsOpen}
                        onClose={() => setTableSettingsOpen(false)}
                        visibleFields={visibleColumns.map((col) => (col.id === 'organization' ? { ...col, label: labels.organization.singular } : col))}
                        hiddenFields={hiddenColumns}
                        onFieldsChange={handleColumnsChange}
                        sortBy={sortBy}
                        onSortByChange={setSortBy}
                        sortOptions={SORT_OPTIONS}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                <ActiveFilterBadges
                  activeFilters={activeFilters}
                  filterGroups={enrichedFilterGroups}
                  onRemoveFilter={handleRemoveFilter}
                  onClearAll={handleClearAllFilters}
                  onOpenFilterSidebar={() => setFilterSidebarOpen(true)}
                />
                <span className="text-muted small">
                  {totalEntries.toLocaleString()} {totalLabel}
                </span>
              </div>

              <Table
                columns={tableColumns}
                data={organizations}
                getRowKey={(organization) => organization.id}
                loading={loading}
                loadingLabel={`Loading ${orgPluralLower}...`}
                error={error}
                empty={<EmptyState size="sm" title={`No ${orgPluralLower} yet.`} />}
                stickyFirstColumn
                stickyLastColumn
                stackBelow={640}
              />

              <TeamPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalEntries}
                pageSize={perPage}
                itemLabel={orgPluralLower}
                onPageChange={(page) => {
                  if (!loading && page >= 1 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
              />
            </div>
          </div>

      <OrganizationFormModal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        organization={formModalOrganization}
        team={team}
        csrfToken={csrfToken}
        customFieldDefinitions={customFieldDefinitions}
        onSaved={handleOrganizationSaved}
      />

      <FilterSidebar
        isOpen={filterSidebarOpen}
        onClose={() => setFilterSidebarOpen(false)}
        onApply={handleApplyFilters}
        title={`Filter ${labels.organization.plural}`}
        subtitle={`Apply filters to find specific ${orgPluralLower}.`}
        filterGroups={enrichedFilterGroups}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
      />

      {organizationToRemove && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 480 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">{`Delete ${orgSingularLower}`}</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={closeRemoveModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="team-modal-body">
              {removeError && (
                <div className="alert alert-danger py-2 mb-2">{removeError}</div>
              )}
              <p className="mb-0">Are you sure? This cannot be undone.</p>
            </div>
            <div className="team-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeRemoveModal}
                disabled={removeSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmRemove}
                disabled={removeSubmitting}
              >
                {removeSubmitting ? 'Deleting…' : `Delete ${orgSingularLower}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmPageLayout>
  );
}

export default OrganizationsPage;
